import { combineReducers, createStore } from 'redux'
import { configure } from '../api'
import { Provider } from 'react-redux'
import { IntlProvider } from 'react-intl'
import ReactPlayer from 'react-player'
import { departureDate } from '../actions/dive-operator-actions'
import get from 'lodash/get'
import {
  pageLoader,
  selectedCurrency,
  setCurrency,
} from '../actions/main-actions'
import {
  adventureAvailability,
  adventureDate,
} from '../actions/adventure-actions'
import {
  collapseItems,
  commentTooltip,
  currencyDirective,
  openToggler,
  originalTitleTooltip,
  stickyBottomNavigation,
} from '../directives'
import { getLanguage, getTranslations } from 'utils'
import 'babel-polyfill'
import 'whatwg-fetch'

import HeroLightbox from '../components/liveaboard/HeroLightbox'
import BackSectionDeparture from '../components/resort/BackSectionDeparture'
import ErrorHandler from '../components/ErrorHandler'
import OtherAdventures from '../components/adventures/OtherAdventures'
import NavigationMap from '../components/covid/NavigationMap'
import AdventurePricing from '../components/adventures/AdventurePricing'
import DiveCenterPhotos from '../components/adventures/DiveCenterPhotos'
import HeroGalleryMobile from '../components/helpers/HeroGalleryMobile'
import PriceConverter, {
  convertCurrency,
} from '../components/helpers/PriceConverter'
import '@formatjs/intl-getcanonicallocales/polyfill'
import '@formatjs/intl-locale/polyfill'
import '@formatjs/intl-pluralrules/polyfill'
import '@formatjs/intl-pluralrules/locale-data/en'
import 'finally-polyfill'
import { createRoot } from 'react-dom/client'
import { trackGA4Ecommerce } from '../gtm'
import { getProductCategory } from '../components/dive-guides/gtm-events'
import store2 from 'store2'
import AdventureDepartureDate from '../components/adventures/AdventureDepartureDate'
import TranslationRating from '../components/helpers/translationRating/TranslationRating'
import AdventureReviewsSection from '../components/adventures/reviews/AdventureReviewsSection'

import 'dayjs/locale/de'
import 'dayjs/locale/fr'
import 'dayjs/locale/it'
import 'dayjs/locale/es'

if(process.env.SENTRY_KEY) {
  Sentry.init({
    dsn: process.env.SENTRY_KEY,
    ignoreErrors: [
      'AbortError',
      'UnhandledRejection',
      'Unexpected token o in JSON at position 1',
      'Anonymous function(/gtm.js)',
      "undefined is not an object (evaluating 'd.appendChild')",
      'chrome-extension',
      'gtm',
      'Illegal invocation',
      "Can't find variable: setIOSParameters",
      '(app-modern)',
      'd is undefined',
    ],
  })
}

let store = createStore(
  combineReducers({
    pageLoader,
    selectedCurrency,
    departureDate,
    adventureDate,
    adventureAvailability,
  }),
)

let language = getLanguage()
let messages = {}

window.operatorStore = store
window.store = store
configure(store)

if(language !== 'en') {
  getTranslations(language, renderApp)
} else {
  renderApp()
}

function renderApp(translatedStrings) {
  if(translatedStrings) {
    messages = translatedStrings
  }
  const map = document.getElementById('navigation-map')
  const videoContainer = document.getElementById('#video-container')
  let mapOptions = {}
  if(map) {
    mapOptions = {
      isDraggable: false,
      latitude: map.getAttribute('latitude'),
      longitude: map.getAttribute('longitude'),
    }
  }

  renderElement('hero-lightbox', HeroLightbox, {
    photos: window.photos,
    videos: window.videos,
  })

  renderElement('video-player', ReactPlayer, {
    url: window.coverVideo && window.coverVideo.url,
    key: '1',
    volume: 1,
    loop: true,
    playing: true,
    controls: false,
    muted: true,
    width: '100%',
    height: videoContainer ? `${videoContainer.offsetHeight}px` : '100%',
    className: 'player',
    config: {
      vimeo: {
        playerOptions: { background: true },
      },
      youtube: {
        playerVars: { disablekb: 1 },
      },
    },
  })

  renderElement('adventures-reviews', AdventureReviewsSection, {
    activityId: window.activityId,
  })

  renderElement('translate_rating', TranslationRating, {
    translationIds: window.translationIds,
  })

  if(window.innerWidth < 768) {
    renderElement('collage-photos', HeroGalleryMobile, {
      photos: window.photos,
    })
  }
  // renderElement('other-diveoperators', OtherGroupTrips, window.shop)
  renderElement('back-section-departure', BackSectionDeparture)
  renderElement('other-adventures-container', OtherAdventures, {
    activityId: window.activityId,
    sectionId: 'other-adventures',
  })
  renderElement('navigation-map', NavigationMap, {
    ...mapOptions,
    countryCode: window.countryCode,
  })
  renderElement('dc-media', DiveCenterPhotos, {
    images: window.diveCenterImages,
  })
  renderElement('adventure-pricing', AdventurePricing, {
    activityId: window.activityId,
    notIncludedInPrice: window.notIncludedInPrice,
    acitivityPricing: window.acitivityPricing,
  })
  renderElement('activity_departure', AdventureDepartureDate, {})

  if(window.acitivityPricing && window.acitivityPricing.price) {
    renderElement('minimum-price', PriceConverter, {
      amount: +window.acitivityPricing.price.origin_value,
      currency: window.acitivityPricing.price.origin_currency,
      conversionPercent: true,
      decimals: true,
    })
  }

  if(
    window.acitivityPricing &&
    window.acitivityPricing.comparePrice.origin_value !==
      window.acitivityPricing.price.origin_value
  ) {
    renderElement('compared-price', PriceConverter, {
      amount: +window.acitivityPricing.comparePrice.origin_value,
      currency: window.acitivityPricing.comparePrice.origin_currency,
      conversionPercent: true,
      decimals: true,
    })
  }
  currencyDirective(function(currency) {
    store.dispatch(setCurrency(currency))
  })
  // convertDjangoRenderedPrice(renderElement, true)
  commentTooltip(renderElement)
  originalTitleTooltip(renderElement)
  openToggler()
  collapseItems()
  stickyBottomNavigation()

  try {
    const originValue = get(window, 'acitivityPricing.price.origin_value', 0)
    const originCurrency = get(
      window,
      'acitivityPricing.price.origin_currency',
      '',
    )
    const convertedPrice = convertCurrency(
      originValue,
      originCurrency,
      'USD',
      false,
      true,
    )
    const { type } = getProductCategory(window.activityType)
    const ecommerce = {
      item_name: window.activityTitle,
      item_id: window.activityId.toString(),
      price: Number(convertedPrice.toFixed(2)),
      item_brand: 'PADI',
      item_category: 'PADI Travel',
      item_category2: type,
      item_category3: `${window.standardProductName}`,
      item_category4: window.affiliateId,
      item_category5: '',
      item_variant: '',
      bundle_name: '',
      bundle_id: '',
      item_list_name: 'PADI Travel Adventures - Product Page',
      quantity: 1,
    }
    trackGA4Ecommerce(
      'view_item',
      {
        currency: 'USD',
        value: Number(convertedPrice.toFixed(2)),
        items: [ecommerce],
      },
      'PADI Travel - Adventures',
    )
    store2.session.set('ecommerce', ecommerce)
  } catch (e) {
    console.error(`Error to push view_item event`, e)
  }
}

function renderElement(elem, ComponentToRender, props) {
  let element
  if(typeof elem === 'string') {
    element = document.getElementById(elem)
  } else {
    element = elem
  }

  if(element) {
    const root = createRoot(element)
    root.render(
      <IntlProvider
        locale={language === 'ach' ? 'en' : language}
        messages={messages}
        textComponent='span'
      >
        <Provider store={store}>
          <ErrorHandler>
            <ComponentToRender {...props} />
          </ErrorHandler>
        </Provider>
      </IntlProvider>,
    )
  }
}
